import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Badge, Card, CardContent, Grid, Stack, Chip, DraggableContainer, DraggableItem, ListItemIcon} from 'views/components';
import {DragIndicator} from 'views/icons';
import {Translate, translate} from 'views/module';

import CaregiverList from './caregiver_selection/CaregiverList';

import moment from 'moment';

const Review = (props) => {
  const {setIsStepValid, newJob, selectedCaregivers, setSelectedCaregivers, updateJobPriorities} = props;

  // Safe defaults for arrays to avoid undefined access
  const requirements = newJob.requirements || {};
  const certifications = requirements.certifications || [];
  const atLeastOneCertifications = requirements.atLeastOneCertifications || [];
  const niceToHaveCertifications = requirements.niceToHaveCertifications || [];
  const languages = requirements.languages || [];
  const niceToHaveLanguages = requirements.niceToHaveLanguages || [];

  const careNeeds = newJob.carePlan?.careNeeds || [];
  const additionalConsiderations = newJob.considerations?.additionalConsiderations?.blob || '';

  const ATTRIBUTE_KEYS = [
    'HOURLY_RATE',
    'START_DATE',
    'CERTIFICATIONS',
    'LANGUAGES',
    'CONSIDERATIONS',
  ];

  const attributeLabelMap = {
    HOURLY_RATE: translate('career.create.Price.HourlyRateLabel'),
    START_DATE: translate('career.create.StartDate.StartDate'),
    CERTIFICATIONS: translate('career.create.Requirements.CertificationLabel'),
    LANGUAGES: translate('career.create.Requirements.LanguagesDisplayLabel'),
    CONSIDERATIONS: translate('career.create.Considerations.Considerations'),
  };

  const [showDragHandle, setShowDragHandle] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  const [orderedAttributes, setOrderedAttributes] = React.useState(
    newJob.priorities?.length ? newJob.priorities : ATTRIBUTE_KEYS,
  );

  // Handler for drag-and-drop reordering — update local state and persist keys
  const handleAttributeDrop = React.useCallback(({removedIndex, addedIndex}) => {
    if (removedIndex === addedIndex) return;
    setOrderedAttributes((prev) => {
      const updated = prev.slice();
      updated.splice(addedIndex, 0, updated.splice(removedIndex, 1)[0]);
      updateJobPriorities(() => updated);
      return updated;
    });
  }, [updateJobPriorities]);

  React.useEffect(() => {
    setIsStepValid(true);
  }, [setIsStepValid]);

  React.useEffect(() => {
    if (!newJob.priorities?.length) {
      updateJobPriorities(() => orderedAttributes);
    }
  }, [newJob.priorities, orderedAttributes, updateJobPriorities]);

  const apiSchedule = React.useMemo(() => {
    const scheduleType = newJob.schedule?.type || '';
    const scheduleData = newJob.schedule?.schedule || {};

    if (scheduleType === 'RECURRENCE_EVENT') {
      return {
        type: scheduleType,
        recurrenceRule: {
          weeklySlots: scheduleData.weeklySlots || [],
          timezone: scheduleData.timezone || '',
        },
      };
    }

    if (scheduleType === 'LIVEIN_EVENT') {
      return {
        type: scheduleType,
      };
    }

    if (scheduleType === 'FLEXIBLE') {
      const flexibleSchedule = {
        type: scheduleType,
        flexibleSchedule: {
          preferredDays: scheduleData.preferredDays || [],
          preferredTimes: scheduleData.preferredTimes || [],
        },
      };

      if ((scheduleData.visitDuration || '').trim()) {
        flexibleSchedule.flexibleSchedule.visitDuration = scheduleData.visitDuration.trim();
      }

      return flexibleSchedule;
    }

    return {
      type: scheduleType,
    };
  }, [newJob.schedule]);
  const scheduleMetrics = React.useMemo(() => (
    (() => {
      const hourlyRate = parseFloat(newJob.price?.hourlyRate) || 0;

      if (!apiSchedule?.type) {
        return {
          totalHours: 0,
          shiftCount: 0,
          weeklyPay: null,
          hasEstimate: false,
        };
      }

      if (apiSchedule.type === 'LIVEIN_EVENT') {
        const totalHours = 56;
        return {
          totalHours,
          shiftCount: 7,
          weeklyPay: totalHours * hourlyRate,
          hasEstimate: true,
        };
      }

      if (apiSchedule.type === 'RECURRENCE_EVENT') {
        const weeklySlots = apiSchedule.recurrenceRule?.weeklySlots || [];
        const totalHours = weeklySlots.reduce((total, slot) => {
          const start = moment(slot.startTime);
          const end = moment(slot.endTime);
          const hoursPerSlot = end.diff(start, 'hours', true);

          return total + Math.abs(hoursPerSlot);
        }, 0);

        return {
          totalHours,
          shiftCount: weeklySlots.length,
          weeklyPay: totalHours * hourlyRate,
          hasEstimate: true,
        };
      }

      if (apiSchedule.type === 'FLEXIBLE') {
        const flexibleSchedule = apiSchedule.flexibleSchedule || {};
        const shiftCount = (flexibleSchedule.preferredDays || []).length;
        const durationText = `${flexibleSchedule.visitDuration ?? ''}`.trim();
        const parsedHours = Number.parseFloat(durationText.replace(/[^0-9.]/g, ''));
        const totalHours = (Number.isFinite(parsedHours) ? parsedHours : 0) * shiftCount;
        const hasEstimate = totalHours > 0 && shiftCount > 0;

        return {
          totalHours,
          shiftCount,
          weeklyPay: hasEstimate ? totalHours * hourlyRate : null,
          hasEstimate,
        };
      }

      return {
        totalHours: 0,
        shiftCount: 0,
        weeklyPay: null,
        hasEstimate: false,
      };
    })()
  ), [apiSchedule, newJob.price?.hourlyRate]);
  const formatSchedule = React.useMemo(() => {
    if (!apiSchedule?.type) {
      return '';
    }

    if (apiSchedule.type === 'RECURRENCE_EVENT') {
      return (apiSchedule.recurrenceRule?.weeklySlots || []).map((slot) => {
        const dayName = translate(`career.create.Schedule.Day.${slot.day}`);
        const startTime = moment(slot.startTime).format('h:mm A');
        const endTime = moment(slot.endTime).format('h:mm A');
        const isOvernight = moment(slot.endTime).isAfter(moment(slot.startTime), 'day');
        const overnightLabel = isOvernight ? ' (overnight)' : '';

        return `${dayName}${overnightLabel}: ${startTime} - ${endTime}`;
      }).join('\n');
    }

    if (apiSchedule.type === 'LIVEIN_EVENT') {
      return translate('career.create.Schedule.ScheduleType.LIVEIN_EVENT');
    }

    if (apiSchedule.type === 'FLEXIBLE') {
      const flexibleSchedule = apiSchedule.flexibleSchedule || {};
      const preferredDays = (flexibleSchedule.preferredDays || [])
          .map((value) => translate(`career.create.Schedule.PreferredDayOptions.${value}`))
          .join(', ');
      const preferredTimes = (flexibleSchedule.preferredTimes || [])
          .map((value) => translate(`career.create.Schedule.PreferredTimeOptions.${value}`))
          .join(', ');
      const lines = [translate('career.create.Schedule.ScheduleType.FLEXIBLE')];

      if (preferredDays) {
        lines.push(translate('career.create.Schedule.PreferredDaysDisplay', {value: preferredDays}));
      }

      if (preferredTimes) {
        lines.push(translate('career.create.Schedule.PreferredTimesDisplay', {value: preferredTimes}));
      }

      if ((flexibleSchedule.visitDuration || '').trim()) {
        lines.push(translate('career.create.Schedule.VisitDurationDisplay', {value: flexibleSchedule.visitDuration.trim()}));
      }

      return lines.join('\n');
    }

    return '';
  }, [apiSchedule]);

  const hourlyRate = parseFloat(newJob.price?.hourlyRate || 0);
  const weeklyEarnings = scheduleMetrics.hasEstimate ? parseFloat((scheduleMetrics.weeklyPay || 0).toFixed(2)) : null;

  return (
    <>
      <Box sx={{p: 3, maxWidth: 900, mx: 'auto'}}>
        <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4, fontWeight: 600}}>
          <Translate expression="career.create.Steps.Review" />
        </Typography>
        <Stack spacing={3}>
          {/* About this role */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.AboutThisRole" />
              </Typography>

              <Box sx={{mb: 2}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                  <Translate expression="career.create.Review.ClientFor" />
                </Typography>
                <Typography variant="body1" sx={{mb: 2}}>
                  {newJob.client.clientName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                  <Translate expression="career.create.Review.Description" />
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                  {newJob.description?.short}
                </Typography>
                {
                  newJob.description?.long?.blob &&
                <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>
                  {newJob.description.long.blob}
                </Typography>
                }
              </Box>
            </CardContent>
          </Card>

          {/* Schedule and Compensation */}
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} sx={{pr: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '140px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.Schedule" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '15px', whiteSpace: 'pre-line'}}>
                    {formatSchedule}
                  </Typography>
                  {
                    scheduleMetrics.hasEstimate ?
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                      <Translate expression="career.create.Review.WeeklyHours" substitute={{hours: scheduleMetrics.totalHours.toFixed(2)}} />
                    </Typography> :
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                      <Translate expression="career.create.Review.WeeklyHoursVary" />
                    </Typography>
                  }
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} sx={{pl: {sm: 1.5}}}>
              <Card sx={{height: '100%', bgcolor: '#fef7f7', minHeight: '140px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.Compensation" />
                  </Typography>
                  <Typography variant="h4" sx={{fontWeight: 'bold', color: '#dc2626', mb: 1}}>
                    <Translate expression="career.create.Review.HourlyRate" substitute={{rate: hourlyRate}} />
                  </Typography>
                  {
                    scheduleMetrics.hasEstimate ?
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '16px'}}>
                      <Translate expression="career.create.Review.WeeklyEarnings" substitute={{amount: weeklyEarnings}} />
                    </Typography> :
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '16px'}}>
                      <Translate expression="career.create.Review.WeeklyEarningsVaries" />
                    </Typography>
                  }
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Location and Start Date */}
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} sx={{pr: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '120px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.Location" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '15px'}}>
                    <Translate
                      expression="career.create.Review.LocationDisplay"
                      substitute={{
                        neighborhood: newJob.location.neighborhood,
                        city: newJob.location.city,
                      }}
                    />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} sx={{pl: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '120px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.StartDate" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '15px'}}>
                    {translate(`career.create.StartDate.Options.${newJob.startDate.startTime}.Label`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                    <Translate expression="career.create.Review.FlexibleStartTiming" />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Care needs */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.CareNeeds" />
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {
                  careNeeds.map((need) => (
                    <Chip
                      key={need}
                      label={translate(`career.create.CarePlan.Options.${need}.Label`)}
                      variant="filled"
                      sx={{bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500, mb: 1}}
                    />
                  ))
                }
              </Stack>
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Steps.Requirements" />
              </Typography>

              {/* Certifications */}
              <Box sx={{mb: 2}}>
                <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                  <Translate expression="career.create.Requirements.CertificationLabel" />
                </Typography>

                {/* Mandatory Certifications */}
                {
                  certifications.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      <Translate expression='career.create.Requirements.ColorKeyMandatory'/>
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        certifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* At Least One Certifications */}
                {
                  atLeastOneCertifications.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyAtLeastOne')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        atLeastOneCertifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FEE5E6', color: '#F9575C', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* Nice to Have Certifications */}
                {
                  niceToHaveCertifications.length > 0 &&
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        niceToHaveCertifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }
              </Box>

              {/* Languages */}
              {
                (languages.length > 0 ||
               niceToHaveLanguages.length > 0) &&
              <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                  <Translate expression="career.create.Requirements.LanguagesDisplayLabel" />
                </Typography>

                {/* Mandatory Languages */}
                {
                  languages.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyMandatory')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        languages.map((lang) => (
                          <Chip
                            key={lang}
                            label={translate(`career.create.Requirements.LanguageOptions.${lang}`)}
                            variant="filled"
                            sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* Nice to Have Languages */}
                {
                  niceToHaveLanguages.length > 0 &&
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        niceToHaveLanguages.map((lang) => (
                          <Chip
                            key={lang}
                            label={translate(`career.create.Requirements.LanguageOptions.${lang}`)}
                            variant="filled"
                            sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }
              </Box>
              }
            </CardContent>
          </Card>

          {/* Special considerations - Only section that can be empty */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.SpecialConsiderations" />
              </Typography>
              <Stack spacing={2}>
                {
                  newJob.considerations?.options?.length > 0 &&
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {
                    newJob.considerations.options.map((option) => (
                      <Chip
                        key={option}
                        label={translate(`career.create.Considerations.Options.${option}`)}
                        variant="filled"
                        sx={{bgcolor: '#fff3e0', color: '#ef6c00', fontWeight: 500, mb: 1}}
                      />
                    ))
                  }
                </Stack>
                }
                {
                  additionalConsiderations &&
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.AdditionalConsiderations" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>
                    {additionalConsiderations}
                  </Typography>
                </Box>
                }
                {
                  (!newJob.considerations?.options?.length && !additionalConsiderations) &&
                <Typography variant="body2" color="text.secondary">
                  <Translate expression="career.create.Review.NoSpecialConsiderations" />
                </Typography>
                }
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={0} alignItems="flex-start">
            {/* Attributes ordering - full width row */}
            <Grid item xs={12} sm={12} sx={{display: 'flex', pr: {sm: 1.5}, mb: {xs: 2, sm: 0}}}>
              <Card sx={{mb: 0, flex: 1, width: '100%'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.OrderAttributes" />
                  </Typography>
                  <Box>
                    <DraggableContainer
                      dragHandleSelector='.drag-handle'
                      lockAxis='y'
                      onDragStart={() => setIsDragging(true)}
                      onDragEnd={() => setIsDragging(false)}
                      onDrop={(params) => {
                        handleAttributeDrop(params); setIsDragging(false);
                      }}
                    >
                      {
                        orderedAttributes.map((key, index) => (
                          <DraggableItem key={`${key}-${index}`}>
                            <Stack sx={{p: 1.25}} >
                              <Stack direction='row' spacing={1} alignItems='center' >
                                <ListItemIcon className='drag-handle' sx={{display: 'flex', justifyContent: 'center'}}>
                                  <Badge
                                    badgeContent={
                                    (isDragging || showDragHandle) ? <DragIndicator/> : index + 1
                                    }
                                    color='primary'
                                    data-testid='taskBadge'
                                    aria-label='task-number'
                                    onMouseOver={() => setShowDragHandle(true)}
                                    onMouseLeave={() => setShowDragHandle(false)}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        'fontSize': '16px',
                                        'height': '32px',
                                        'width': '32px',
                                        'borderRadius': '20px',
                                        'color': (isDragging || showDragHandle) ? 'rgba(0, 0, 0, 0.54)' : undefined,
                                        'bgcolor': (isDragging || showDragHandle) ? 'transparent' : 'primary',
                                        'cursor': isDragging ? 'grabbing' : 'grab',
                                      },
                                    }}
                                  />
                                </ListItemIcon>
                                <Typography>
                                  {attributeLabelMap[key]}
                                </Typography>
                              </Stack>
                            </Stack>
                          </DraggableItem>
                        ))
                      }
                    </DraggableContainer>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    <Translate expression="career.create.Review.OrderAttributesHelp" />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Stack>
      </Box>

      {/* Caregiver Notification Selection - full width outside container */}
      <Box sx={{px: 3, mt: 3}}>
        <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
          <Translate expression="career.create.Review.NotifyCaregivers" />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          <Translate expression="career.create.Review.NotifyCaregiversHelp" />
        </Typography>
        <CaregiverList jobLocation={newJob.location} selectedCaregivers={selectedCaregivers} setSelectedCaregivers={setSelectedCaregivers}/>
      </Box>
    </>
  );
};

Review.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  selectedCaregivers: PropTypes.array,
  setSelectedCaregivers: PropTypes.func,
  updateJobPriorities: PropTypes.func,
};

export default Review;
